import { ExternalLink, Shield, Cpu, Lock, Wrench, FileText } from "lucide-react";

const ROUTER = "https://affiliate.eigooinc.net/go";

const GEAR = [
  {
    id: "agent-z-rig",
    slug: "agent-z-rig",
    name: "Agent-Z Rig",
    tier: "Tier I · High-Performance Hardware",
    icon: Cpu,
    image:
      "https://images.unsplash.com/photo-1587202372775-e229f172b9d7?auto=format&fit=crop&w=940&q=80",
    blurb:
      "The operator's workstation. Silent thermals, brutal compute, sovereign-grade chassis.",
  },
  {
    id: "sovereign-sentinel",
    slug: "sovereign-sentinel",
    name: "Sovereign Sentinel",
    tier: "Tier II · Cybersecurity Bounties",
    icon: Lock,
    image:
      "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=940&q=80",
    blurb:
      "Perimeter intelligence. Threat bounties, red-team coverage, AES-grade hygiene.",
  },
  {
    id: "command-exoskeleton",
    slug: "command-exoskeleton",
    name: "Command Exoskeleton",
    tier: "Tier I · Business Operations Tools",
    icon: Wrench,
    image:
      "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=940&q=80",
    blurb:
      "The operator's outer frame. Books, ledgers, automations — the engine room of the enterprise.",
  },
  {
    id: "master-blueprint",
    slug: "master-blueprint",
    name: "Master Blueprint",
    tier: "Tier 0 · The Armada Manifesto",
    icon: FileText,
    image:
      "https://images.unsplash.com/photo-1517842645767-c639042777db?auto=format&fit=crop&w=940&q=80",
    blurb:
      "The doctrine in long form. Every volume of the Manifesto bound and sealed for command.",
  },
];

export default function AffiliateArmor() {
  return (
    <section id="affiliate-armor" data-testid="affiliate-armor">
      <div className="mb-7 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse-violet" />
            <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-gold/90">
              Affiliate Armor
            </span>
          </div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-white tracking-tight">
            Elite Gear · Operator Approved
          </h2>
          <p className="mt-3 max-w-2xl text-white/55 leading-relaxed">
            Four anchors. Hardware, security, operations, doctrine. Each one
            chosen for the sovereign operator who refuses noise.
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-white/45">
          <Shield className="w-3 h-3 text-gold" /> Vetted · Rotating Roster
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {GEAR.map((g, i) => {
          const Icon = g.icon;
          return (
            <a
              key={g.id}
              href={`${ROUTER}/${g.slug}`}
              target="_blank"
              rel="noopener sponsored"
              data-testid={`affiliate-link-${i + 1}`}
              data-track-slug={g.slug}
              className="group relative rounded-xl overflow-hidden border border-white/8 bg-black/60 transition-all duration-300 hover:border-gold/40 hover:-translate-y-1"
            >
              <div className="aspect-[4/5] overflow-hidden relative">
                <img
                  src={g.image}
                  alt={g.name}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale-[40%] group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute top-3 left-3">
                  <div className="font-mono text-[9px] tracking-[0.24em] uppercase px-2 py-1 rounded border border-violet/40 bg-black/70 text-violet-soft">
                    {g.tier}
                  </div>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full border border-gold/30 bg-black/70 backdrop-blur-md">
                  <Icon className="w-3.5 h-3.5 text-gold" />
                </div>
              </div>

              <div className="p-5">
                <div className="font-heading text-lg text-white leading-tight">
                  {g.name}
                </div>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">
                  {g.blurb}
                </p>

                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold/90 inline-flex items-center gap-1">
                    Acquire <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">
                    · {g.id}
                  </span>
                </div>
              </div>

              <span className="pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          );
        })}
      </div>
    </section>
  );
}
